import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";

const PAGES = ["about", "constitution", "elections", "rnvp"];
const LANG_OPTIONS = [
  { code: "mr", label: "मराठी" },
  { code: "hi", label: "हिंदी" },
  { code: "en", label: "English" },
];
const inputCls = "w-full px-4 py-2.5 rounded-xl border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 text-sm";

export default function PagesEditor() {
  const { t } = useLang();
  const [page, setPage] = useState("about");
  const [lang, setLang] = useState("mr");
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContent(null);
    api
      .get(`/pages/${page}`)
      .then((r) => setContent(r.data.content))
      .catch((e) => toast.error(errMsg(e)));
  }, [page]);

  if (!content) return <p className="text-slate-400 py-6" data-testid="pages-editor-loading">...</p>;

  const sections = content[lang] || [];
  const update = (i, key, val) =>
    setContent({ ...content, [lang]: sections.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) });
  const addSection = () => setContent({ ...content, [lang]: [...sections, { title: "", body: "" }] });
  const removeSection = (i) => setContent({ ...content, [lang]: sections.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/pages/${page}`, { content });
      toast.success(t.profile_saved);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6 sm:p-8" data-testid="pages-editor-panel">
      <div className="flex flex-wrap gap-4 mb-4">
        <select value={page} onChange={(e) => setPage(e.target.value)} className={`${inputCls} max-w-xs`} data-testid="pages-editor-page-select">
          {PAGES.map((p) => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <select value={lang} onChange={(e) => setLang(e.target.value)} className={`${inputCls} max-w-xs`} data-testid="pages-editor-lang-select">
          {LANG_OPTIONS.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-slate-500 mb-5" data-testid="pages-editor-hint">
        {t.pages_hint}
      </p>
      <div className="space-y-6">
        {sections.map((s, i) => (
          <div key={i} className="border border-emerald-100 rounded-xl p-4 space-y-3" data-testid={`pages-editor-section-${i}`}>
            <div className="flex gap-3 items-center">
              <input value={s.title} onChange={(e) => update(i, "title", e.target.value)} placeholder={t.section_title}
                className={inputCls} data-testid={`pages-editor-title-${i}`} />
              <button onClick={() => removeSection(i)} className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors" data-testid={`pages-editor-delete-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <textarea rows={5} value={s.body} onChange={(e) => update(i, "body", e.target.value)} placeholder={t.section_body}
              className={inputCls} data-testid={`pages-editor-body-${i}`} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-6">
        <button onClick={addSection} data-testid="pages-editor-add-section"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-700 text-emerald-800 text-sm font-semibold hover:bg-emerald-50 transition-colors">
          <Plus className="w-4 h-4" /> {t.add_section}
        </button>
        <button onClick={save} disabled={saving} data-testid="pages-editor-save-btn"
          className="px-8 py-2.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all disabled:opacity-60">
          {saving ? "..." : t.save}
        </button>
      </div>
    </div>
  );
}
